import { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../utils/api';
import PageHeader from '../components/PageHeader';
import { Grid3X3, Plus, Settings, X, Printer, Clock, User, Pencil, Trash2, RefreshCw, Zap, Thermometer, Maximize2, CalendarClock, AlertTriangle, Bell, BellOff } from 'lucide-react';
import CameraFeed from '../components/printer/CameraFeed';
import PrinterDetailModal from '../components/printer/PrinterDetailModal';
import ModalPortal from '../components/ModalPortal';
import { FilamentModal, NozzleSelectModal, getErrorMsg } from './PrintersPage';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.locale('ko');
dayjs.extend(relativeTime);

const TOTAL_SLOTS = 32;   // 8 × 4 — 현장 배치도를 옮기기 위한 격자
const COLS = 8;
const ROWS = TOTAL_SLOTS / COLS;

// 카드 라이브뷰 주기. 22대가 한 화면에 깔리므로 주기가 곧 부하다.
// 출력 중인 것만 자주 갱신하고 대기 중은 느리게 본다.
const CAM_POLL_PRINTING = 1000;
const CAM_POLL_IDLE = 20000;

// 프린터 실시간 상태 → 화면 표시용
const STATE_LABEL = { RUNNING: '출력중', PAUSE: '일시정지', FINISH: '완료', IDLE: '대기', FAILED: '실패', PREPARE: '준비중' };
// 배지는 작고 굵은 글자라 대비가 낮으면 바로 안 읽힌다.
// 브랜드색(#00B2FF)·앰버 위의 흰 글자는 대비가 2:1 수준이어서 짙은 글자를 쓴다.
// 배지 색.
// text-white 는 라이트 모드에서 #111827로 치환된다(src/index.css의 html.light 규칙).
// 예외 목록에 없는 배경 위에서는 글자가 검게 나오므로 임의값(#fff)으로 고정한다.
function stateColor(st, offline) {
  if (offline) return { dot: 'bg-gray-500', text: 'text-gray-500', badge: 'bg-gray-600 text-[#ffffff]' };
  if (st === 'RUNNING') return { dot: 'bg-brand', text: 'text-brand', badge: 'bg-brand text-[#06263a]' };
  if (st === 'PAUSE') return { dot: 'bg-amber-400', text: 'text-amber-500', badge: 'bg-amber-400 text-[#3d2704]' };
  if (st === 'FAILED') return { dot: 'bg-red-500', text: 'text-red-500', badge: 'bg-red-600 text-[#ffffff]' };
  return { dot: 'bg-emerald-500', text: 'text-emerald-600', badge: 'bg-emerald-700 text-[#ffffff]' };
}
// 남은 시간. 카드 폭이 좁아 이름을 밀어내므로 길이를 스스로 줄인다.
// 10시간 넘게 남은 작업에 분 단위는 의미 없는 정밀도이기도 하다.
function fmtMinutes(min, compact = false) {
  if (min == null || min <= 0) return null;
  const h = Math.floor(min / 60), m = Math.round(min % 60);
  if (!h) return `${m}분`;
  if (compact || h >= 10) return `${h}시간`;
  return `${h}시간 ${m}분`;
}

function fmtTime(dt) { return `${dayjs(dt).format('H')}시`; }

const DOW = ['일', '월', '화', '수', '목', '금', '토'];

// 시간 범위 포맷: 오늘이면 시간만, 다른 날이면 날짜+요일 포함
function fmtRange(start, end) {
  const s = dayjs(start);
  const e = dayjs(end);
  const todayStr = dayjs().format('YYYY-MM-DD');
  const sDay = s.format('YYYY-MM-DD');
  const eDay = e.format('YYYY-MM-DD');
  const sDow = DOW[s.day()];
  const eDow = DOW[e.day()];
  const sStr = sDay === todayStr ? `${s.format('H')}시` : `${s.format('M/D')}(${sDow}) ${s.format('H')}시`;
  const eStr = eDay === todayStr ? `${e.format('H')}시` : `${e.format('M/D')}(${eDow}) ${e.format('H')}시`;
  return `${sStr} ~ ${eStr}`;
}

// 남은 시간 텍스트
function fmtRemaining(endTime) {
  const diff = dayjs(endTime).diff(dayjs(), 'minute');
  if (diff <= 0) return '곧 종료';
  if (diff < 60) return `${diff}분`;
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// 진행률 (0~100)
function elapsedPct(startTime, endTime) {
  const total = new Date(endTime) - new Date(startTime);
  const elapsed = Date.now() - new Date(startTime);
  return Math.min(100, Math.max(0, (elapsed / total) * 100));
}

function isCurrent(q) {
  const now = Date.now();
  return new Date(q.startTime).getTime() <= now && now <= new Date(q.endTime).getTime();
}
function isPast(q) { return new Date(q.endTime).getTime() < Date.now(); }

/** 배지에 들어갈 짧은 표기. 진행 중이면 남은 시간, 아니면 시작 시점. */
function fmtQueueShort(q) {
  if (!q) return '';
  if (isCurrent(q)) return fmtRemaining(q.endTime);
  const d = dayjs(q.startTime);
  return d.isSame(dayjs(), 'day') ? d.format('H시') : d.format('M/D H시');
}

// ── 큐 아이템 ────────────────────────────────────────────────────────────
function QueueItem({ q, onDelete, onEdit, me }) {
  const current = isCurrent(q);
  const past = isPast(q);
  const canEdit = me && (me.id === q.admin?.id || me.role === 'superadmin');
  const pct = current ? elapsedPct(q.startTime, q.endTime) : 0;

  return (
    <div className={`rounded-lg border overflow-hidden shrink-0
      ${current ? 'bg-brand/15 border-brand/50' : past ? 'bg-gray-800 border-gray-700 opacity-50' : 'bg-gray-800 border-gray-700'}`}>

      {current && (
        <div className="h-0.5 w-full bg-brand/25">
          <div className="h-full bg-brand transition-all duration-[60000ms] ease-linear" style={{ width: `${pct}%` }} />
        </div>
      )}

      <div className="px-2 py-1.5 flex flex-col gap-1">
        {/* 1행: 이름 + 시간 + 조작 — 조작 버튼은 항상 보이게 둔다(터치 기기 고려) */}
        <div className="flex items-center gap-1">
          <span className={`text-[11px] font-semibold truncate flex-1 ${past ? 'text-gray-500' : 'text-gray-100'}`}
                title={q.jobName}>
            {q.jobName}
          </span>
          {canEdit && !past && (
            <>
              <button onClick={() => onEdit(q)} title="수정"
                className="p-0.5 text-gray-500 hover:text-brand shrink-0"><Pencil size={11} /></button>
              <button onClick={() => onDelete(q.id)} title="삭제"
                className="p-0.5 text-gray-500 hover:text-red-500 shrink-0"><Trash2 size={11} /></button>
            </>
          )}
        </div>

        {/* 2행: 시간 · 등록자 — 한 줄로 압축 */}
        <div className="flex items-center gap-1 text-[10px] text-gray-500 min-w-0">
          <Clock size={9} className="shrink-0" />
          <span className={`tabular-nums truncate ${current ? 'text-brand font-semibold' : ''}`}>
            {fmtRange(q.startTime, q.endTime)}
          </span>
          {current && <span className="ml-auto text-brand font-bold tabular-nums shrink-0">{fmtRemaining(q.endTime)}</span>}
          {!current && q.admin?.name && <span className="ml-auto truncate shrink-0">{q.admin.name}</span>}
        </div>

        {q.memo && (
          <div className="text-[10px] text-gray-500 truncate" title={q.memo}>{q.memo}</div>
        )}
      </div>
    </div>
  );
}

// ── 예약 팝오버 ───────────────────────────────────────────────────────────
// 카드는 고정 높이 격자 칸(현장 배치도)이라 안에서 펼칠 수 없다.
// 배지 옆에 띄우고, 격자에 잘리지 않도록 body로 보낸다.
function QueuePopover({ slot, anchorRect, me, onClose, onAdd, onEdit, onDelete }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  // 띄운 뒤 실제 크기를 재서 화면 밖으로 안 나가게 뒤집거나 민다.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !anchorRect) return;
    const { width: w, height: h } = el.getBoundingClientRect();
    const M = 8;
    let top = anchorRect.bottom + 6;
    if (top + h > window.innerHeight - M) top = anchorRect.top - h - 6;      // 아래 공간 없으면 위로
    if (top < M) top = Math.max(M, window.innerHeight - h - M);              // 그래도 안 되면 화면 안으로
    let left = anchorRect.left + anchorRect.width / 2 - w / 2;               // 카드 중앙 정렬
    left = Math.min(Math.max(M, left), window.innerWidth - w - M);
    setPos({ top, left });
  }, [anchorRect]);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onClose);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('resize', onClose); };
  }, [onClose]);

  const queues = (slot.queues || []).filter(q => !isPast(q))
    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  const name = slot.alias || slot.realPrinter?.name || '';

  return createPortal(
    <>
      <div className="fixed inset-0 z-[60]" onClick={onClose} />
      <div ref={ref}
        className="fixed z-[61] w-64 rounded-xl border border-gray-700 bg-gray-900 overflow-hidden"
        style={pos ? { top: pos.top, left: pos.left } : { top: -9999, left: -9999 }}>
        <div className="px-3 py-2 border-b border-gray-800 flex items-center gap-1.5">
          <CalendarClock size={12} className="text-brand shrink-0" />
          <span className="text-xs font-bold text-gray-100 truncate">{name} 예약</span>
          <button onClick={onClose} className="ml-auto text-gray-500 hover:text-gray-200 shrink-0"><X size={13} /></button>
        </div>

        <div className="max-h-64 overflow-y-auto p-2 flex flex-col gap-1.5">
          {queues.length === 0
            ? <div className="py-5 text-center text-[11px] text-gray-600">등록된 예약이 없습니다</div>
            : queues.map(q => (
                <QueueItem key={q.id} q={q} me={me} onDelete={onDelete} onEdit={onEdit} />
              ))}
        </div>

        <button onClick={() => onAdd(slot)}
          className="w-full py-2 text-[11px] border-t border-gray-800 text-gray-400 hover:text-brand hover:bg-gray-800 flex items-center justify-center gap-1 transition-colors">
          <Plus size={11} /> 예약 등록
        </button>
      </div>
    </>,
    document.body,
  );
}

// ── 프린터 카드 ───────────────────────────────────────────────────────────
function PrinterCard({ slot, me, mqtt, camIndex, onOpenQueue, onOpenDetail, isMobile }) {
  const { realPrinter: printer, queues = [], alias } = slot;
  const activeQueues = queues.filter(q => !isPast(q)).sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  const currentQueue = queues.find(isCurrent);
  // 진행 중인 예약이 있으면 그게 '다음'이다. 없으면 가장 이른 예정 건.
  const nextQueue = currentQueue || activeQueues[0] || null;

  if (!printer) {
    return (
      <div className={`border border-dashed border-gray-800 rounded-xl flex items-center justify-center opacity-30 ${isMobile ? 'h-20' : 'h-full'}`}>
        <span className="text-gray-600 text-xs">빈 슬롯</span>
      </div>
    );
  }

  // 화면 표시는 프린터가 실제로 보고하는 상태를 따른다. 큐는 계획일 뿐이다.
  const offline = printer.status === 'offline' || !mqtt;
  const gstate = mqtt?.gcodeState;
  const printing = gstate === 'RUNNING';
  const c = stateColor(gstate, offline);
  const pct = printing ? Math.max(0, Math.min(100, Number(mqtt?.mcPercent) || 0)) : 0;
  const remain = printing ? fmtMinutes(mqtt?.mcRemainingTime) : null;
  // "몇 시에 끝나는지"가 붙으면 다음 작업을 잡기 쉽다
  const eta = printing && mqtt?.mcRemainingTime > 0
    ? (() => { const d = new Date(Date.now() + mqtt.mcRemainingTime * 60000);
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')} 완료`; })()
    : null;

  // 반자동 이어가기.
  // 출력이 끝난 프린터에 아직 안 보낸 파일 큐가 남아 있으면 화면이 먼저 알린다.
  // 자동으로 보내지 않는 이유: 베드에 조형물이 그대로 있으면 다음 출력이 충돌한다.

  // 프린터가 보고하는 오류. 출력 실패(printError)는 즉시 조치가 필요하고,
  // HMS는 윤활·청소 같은 정비 알림이라 급하지는 않다. 둘을 구분해서 보여준다.
  const fatal = mqtt?.printErrorMsg || (gstate === 'FAILED' ? '출력에 실패했습니다.' : null);
  const hms = mqtt?.hmsErrors || [];
  // 카메라가 세로를 너무 먹으면 큐가 한 건도 안 들어간다. 목록에서는 식별만 되면 충분하다.
  // 큐가 빠지면 카드에 채울 게 줄어든다. 관제 전용일 때는 영상을 크게 본다.
  // 영상 높이는 상태와 무관하게 고정한다. flex로 채우면 출력 중 카드만 진행 영역만큼
  // 영상이 줄어들어 격자가 들쭉날쭉해진다.
  // 온도·알림 줄을 모든 카드가 똑같이 갖도록 만들어 두었으므로(아래 참조)
  // 카메라가 남은 높이를 다 먹어도 카드끼리 높이가 어긋나지 않는다.
  // 고정 %로 두면 화면이 높을 땐 아래가 비고 낮을 땐 마지막 줄이 잘린다.
  const camH = isMobile ? 'h-40' : 'flex-1 min-h-0';

  return (
    <div className={`rounded-xl overflow-hidden flex flex-col transition-all duration-300
      ${isMobile ? 'min-h-[260px]' : 'h-full'}
      ${printing
        ? 'border-2 border-brand ring-2 ring-brand/35 bg-brand/[0.07]'
        : offline ? 'border border-gray-800 bg-gray-900 opacity-70'
                  : 'border border-gray-800 bg-gray-900'}`}>

      {/* 라이브뷰 — 누르면 상세 */}
      <button
        onClick={() => onOpenDetail(printer)}
        className={`relative shrink-0 w-full ${camH} group overflow-hidden`}
        title={`${printer.name} 상세 보기`}>
        {offline ? (
          <div className="w-full h-full bg-gray-800/60 flex flex-col items-center justify-center gap-1">
            <Printer size={22} className="text-gray-700" />
            <span className="text-[10px] text-gray-600">오프라인</span>
          </div>
        ) : (
          <CameraFeed
            printerId={printer.id}
            cameraStyle={printer.cameraStyle}
            // 출력 중인 프린터만 또렷하게. 나머지는 눌러서 한눈에 구분되게 한다.
            // (상세 화면에서는 원본 그대로 보여준다)
            // 출력 중인 프린터만 원본 그대로. 나머지는 채도를 낮춰 파란 틴트를 받도록 한다.
            // (어둡게만 하면 챔버 안이 아예 안 보인다)
            className={`w-full h-full transition-all duration-500 ${printing ? '' : 'saturate-[0.35] brightness-90'}`}
            pollInterval={printing ? CAM_POLL_PRINTING : CAM_POLL_IDLE}
            pollDelay={(camIndex % 8) * 120}
          />
        )}

        {/* 출력 중이 아닌 프린터: 파란 틴트로 구분. 동시에 이름 가독성도 확보된다.
            출력 중이면 영상을 가리지 않도록 아무것도 덮지 않는다. */}
        {!printing && !offline && (
          <div className="absolute inset-0 bg-[#0b3c5d] opacity-45 pointer-events-none" />
        )}
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />

        {/* 상태 배지 */}
        <div className={`absolute top-1.5 right-1.5 flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${c.badge}`}>
          {printing && <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />}
          {offline ? '오프라인' : (STATE_LABEL[gstate] || '대기')}
        </div>

        {/* 오류 표시 — 왜 멈췄는지 카드에서 바로 보이게 한다 */}
        {(fatal || hms.length > 0) && (
          <div className={`absolute top-1.5 left-1.5 flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full
            ${fatal ? 'bg-red-600 text-[#ffffff]' : 'bg-amber-400 text-[#3d2704]'}`}
            title={fatal || hms.map(h => h.msg).join('\n')}>
            <AlertTriangle size={9} />
            {fatal ? '오류' : hms.length}
          </div>
        )}

        {/* 확대 힌트 */}
        <div className="absolute top-1.5 left-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <Maximize2 size={11} className="text-white/70" />
        </div>

        {/* 이름 + 남은 시간. 관제 화면에서 제일 궁금한 건 "언제 비나"라 크게 둔다. */}
        <div className="absolute bottom-0 left-0 right-0 px-2.5 pb-1.5 text-left min-w-0">
          <div className={`font-black leading-tight truncate ${isMobile ? 'text-base' : 'text-sm xl:text-base 2xl:text-lg'} ${offline ? 'text-[#9ca3af]' : 'text-[#ffffff]'}`}
            title={alias || printer.name}>
            {alias || printer.name}
          </div>
          {printing ? (
            <div className="flex items-baseline gap-1.5 min-w-0">
              <span className={`font-black text-[#ffffff] tabular-nums whitespace-nowrap shrink-0
                ${isMobile ? 'text-lg' : 'text-base xl:text-lg 2xl:text-xl'}`}>
                {remain || '—'}
              </span>
              {/* 완료 시각은 폭이 날 때만. 좁으면 '17:0…'으로 잘려 오히려 헷갈린다.
                  대신 진행률을 보여준다 — 짧고 항상 들어간다. */}
              <span className="hidden 2xl:inline text-[10px] text-[#cbd5e1] tabular-nums truncate">{eta || `${pct}%`}</span>
              <span className="2xl:hidden text-[10px] text-[#cbd5e1] tabular-nums shrink-0">{pct}%</span>
            </div>
          ) : (
            alias && <div className="text-[10px] text-[#d1d5db] truncate">{printer.name}</div>
          )}
          {/* 작업 파일명은 넓은 화면에서만. 좁은 폭에서는 '2609...'로 잘려 정보가 못 된다. */}
          {printing && mqtt?.jobName && (
            <div className="hidden 2xl:block text-[10px] text-[#cbd5e1] truncate" title={mqtt.jobName}>
              {mqtt.jobName}
            </div>
          )}
        </div>

        {/* 진행률 — 영상 맨 아래 가장자리 */}
        {printing && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/40">
            <div className="h-full bg-brand transition-all duration-1000" style={{ width: `${pct}%` }} />
          </div>
        )}
      </button>

      {/* 온도 */}
      {(!offline || !isMobile) && (
        <div className={`shrink-0 px-2.5 py-1 flex items-center gap-2 text-[10px] border-b border-gray-800/60
          ${offline ? 'text-gray-600' : 'text-gray-500'}`}>
          <Thermometer size={9} className="shrink-0" />
          <span className="tabular-nums">노즐 {!offline && mqtt?.nozzleTemper != null ? Math.round(mqtt.nozzleTemper) : '-'}°</span>
          <span className="tabular-nums">베드 {!offline && mqtt?.bedTemper != null ? Math.round(mqtt.bedTemper) : '-'}°</span>
        </div>
      )}

      {/* 오류·정비 알림은 온도 아래에 둔다.
          위에 두면 알림 있는 카드만 온도 줄이 밀려 격자가 어긋난다.
          알림이 없어도 자리는 늘 비워둔다 — 그래야 알림이 생긴 카드에서
          마지막 줄이 카드 밖으로 밀려 잘리지 않는다. */}
      {!isMobile && (
        <div className={`shrink-0 px-2.5 py-1 min-h-[22px] flex items-center
          ${fatal ? 'bg-red-500/15' : hms.length > 0 ? 'bg-amber-400/15' : ''}`}>
          {/* 한 줄로 자른다. 두 줄이면 카드 높이를 넘어 잘린 채 보인다. 전체는 상세에서. */}
          {fatal ? (
            <p className="text-[10px] text-red-600 leading-tight truncate" title={fatal}>{fatal}</p>
          ) : hms.length > 0 ? (
            <p className="text-[10px] text-amber-600 leading-tight truncate" title={hms.map(h => h.msg).join('\n')}>
              정비 알림 {hms.length}건 · {hms[0].msg}
            </p>
          ) : null}
        </div>
      )}
      {isMobile && fatal && (
        <div className="shrink-0 px-2.5 py-1 bg-red-500/15">
          <p className="text-[10px] text-red-600 leading-tight truncate" title={fatal}>{fatal}</p>
        </div>
      )}
      {isMobile && !fatal && hms.length > 0 && (
        <div className="shrink-0 px-2.5 py-1 bg-amber-400/15">
          <p className="text-[10px] text-amber-600 leading-tight truncate" title={hms.map(h => h.msg).join('\n')}>
            정비 알림 {hms.length}건 · {hms[0].msg}
          </p>
        </div>
      )}

      {/* 예약 — 접어둔다.
          카드가 고정 높이 격자 칸이라 안에서 펼치면 현장 배치도가 깨지고,
          라이브뷰도 그만큼 줄어든다. 여기서는 건수와 다음 일정만 알리고
          자세한 건 눌러서 띄운다. 알림 줄과 마찬가지로 예약이 없어도
          자리는 비워둬야 카드끼리 높이가 어긋나지 않는다. */}
      <button
        onClick={e => { e.stopPropagation(); onOpenQueue(slot, e.currentTarget); }}
        title={nextQueue ? `${nextQueue.jobName} · ${fmtRange(nextQueue.startTime, nextQueue.endTime)}` : '예약 등록'}
        className={`shrink-0 w-full px-2.5 py-1 min-h-[22px] flex items-center gap-1 text-[10px] text-left
          transition-colors border-t border-gray-800/60
          ${currentQueue ? 'bg-brand/15 text-brand hover:bg-brand/25' : 'text-gray-500 hover:bg-gray-800'}`}>
        <CalendarClock size={9} className="shrink-0" />
        {activeQueues.length === 0 ? (
          <span className="truncate">예약 없음</span>
        ) : (
          <>
            <span className="font-semibold shrink-0 tabular-nums">예약 {activeQueues.length}</span>
            <span className="truncate">{nextQueue?.jobName}</span>
            <span className="ml-auto shrink-0 tabular-nums">{fmtQueueShort(nextQueue)}</span>
          </>
        )}
      </button>
    </div>
  );
}

// ── 큐 모달 ──────────────────────────────────────────────────────────────
function DateHourPicker({ date, hour, onDateChange, onHourChange, label }) {
  const inputCls = "bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand transition-colors";
  return (
    <div>
      <label className="text-xs text-gray-400 mb-1.5 block">{label}</label>
      <div className="flex gap-1.5">
        <input
          type="date"
          value={date}
          onChange={e => onDateChange(e.target.value)}
          required
          className={`flex-1 ${inputCls}`}
        />
        <select
          value={hour}
          onChange={e => onHourChange(e.target.value)}
          className={`w-20 ${inputCls}`}
        >
          {Array.from({ length: 24 }, (_, i) => (
            <option key={i} value={String(i).padStart(2, '0')}>
              {String(i).padStart(2, '0')}시
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function QueueModal({ slot, editTarget, onClose, onSaved }) {
  const nowHour = dayjs().startOf('hour');
  const parsedt = dt => ({ date: dayjs(dt).format('YYYY-MM-DD'), hour: dayjs(dt).format('HH') });
  const initStart = editTarget ? parsedt(editTarget.startTime) : { date: nowHour.format('YYYY-MM-DD'), hour: nowHour.format('HH') };
  const initEnd   = editTarget ? parsedt(editTarget.endTime)   : { date: nowHour.add(1, 'hour').format('YYYY-MM-DD'), hour: nowHour.add(1, 'hour').format('HH') };

  const [jobName, setJobName] = useState(editTarget?.jobName || '');
  const [startDate, setStartDate] = useState(initStart.date);
  const [startHour, setStartHour] = useState(initStart.hour);
  const [endDate, setEndDate]     = useState(initEnd.date);
  const [endHour, setEndHour]     = useState(initEnd.hour);
  const [memo, setMemo]           = useState(editTarget?.memo || '');
  const [loading, setLoading]     = useState(false);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const startTime = new Date(`${startDate}T${startHour}:00:00`).toISOString();
      const endTime   = new Date(`${endDate}T${endHour}:00:00`).toISOString();
      if (new Date(endTime) <= new Date(startTime)) {
        alert('종료 시각이 시작 시각보다 늦어야 합니다.');
        return;
      }
      const payload = { jobName, startTime, endTime, memo };
      if (editTarget) {
        await api.put(`/printing-board/queue/${editTarget.id}`, payload);
      } else {
        await api.post(`/printing-board/slots/${slot.slotIndex}/queue`, payload);
      }
      onSaved();
    } catch (err) {
      alert(err.response?.data?.error || '오류 발생');
    } finally { setLoading(false); }
  }

  const inputCls = "w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white caret-white focus:outline-none focus:border-brand transition-colors placeholder-gray-600";

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700/50 rounded-2xl w-full max-w-sm p-5 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-bold text-gray-100">{editTarget ? '예약 수정' : '예약 등록'}</h3>
            {slot && <p className="text-xs text-gray-500 mt-0.5">{slot.alias || slot.printer?.modelName}</p>}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-3">
          <div>
            <label className="text-xs text-gray-400 mb-1.5 block">작업명 *</label>
            <input value={jobName} onChange={e => setJobName(e.target.value)} required className={inputCls} placeholder="예: Xinvana A형 250개" />
          </div>
          <DateHourPicker label="시작 *" date={startDate} hour={startHour} onDateChange={setStartDate} onHourChange={setStartHour} />
          <DateHourPicker label="종료 *" date={endDate}   hour={endHour}   onDateChange={setEndDate}   onHourChange={setEndHour} />
          <div>
            <label className="text-xs text-gray-400 mb-1.5 block">메모</label>
            <input value={memo} onChange={e => setMemo(e.target.value)} className={inputCls} placeholder="선택사항" />
          </div>

          <button type="submit" disabled={loading}
            className="w-full bg-brand text-white rounded-xl py-2.5 text-sm font-bold hover:bg-brand/80 disabled:opacity-50 transition-colors mt-1">
            {loading ? '저장 중...' : editTarget ? '수정 완료' : '등록'}
          </button>
        </form>
      </div>
    </div>
    </ModalPortal>
  );
}

// ── 관리자 모달 ───────────────────────────────────────────────────────────
function AdminModal({ onClose, onSaved }) {
  const [printers, setPrinters] = useState([]);
  const [slots, setSlots] = useState(Array(TOTAL_SLOTS).fill(null));
  const [newModel, setNewModel] = useState('');
  const [newPhoto, setNewPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editSlot, setEditSlot] = useState(null);
  const [editSlotForm, setEditSlotForm] = useState({ realPrinterId: '', alias: '' });

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    // 기종 마스터가 아니라 실제 관제 대상 프린터를 고른다
    const [pRes, bRes] = await Promise.all([
      api.get('/printers'),
      api.get('/printing-board/board'),
    ]);
    setPrinters(Array.isArray(pRes.data) ? pRes.data : (pRes.data?.rows || []));
    setSlots(bRes.data);
  }

  async function addPrinter(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('modelName', newModel);
      if (newPhoto) fd.append('photo', newPhoto);
      await api.post('/printing-board/printers', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setNewModel(''); setNewPhoto(null);
      await loadData();
    } catch (err) { alert(err.response?.data?.error || '오류'); }
    finally { setSaving(false); }
  }

  async function deletePrinter(id) {
    if (!confirm('삭제할까요?')) return;
    await api.delete(`/printing-board/printers/${id}`);
    await loadData();
  }

  function openSlotEdit(idx) {
    const s = slots[idx];
    setEditSlot(idx);
    setEditSlotForm({ realPrinterId: s?.realPrinterId || '', alias: s?.alias || '' });
  }

  async function saveSlot() {
    setSaving(true);
    try {
      await api.put(`/printing-board/slots/${editSlot}`, editSlotForm);
      setEditSlot(null);
      await loadData();
      onSaved();
    } catch (err) { alert(err.response?.data?.error || '오류'); }
    finally { setSaving(false); }
  }

  async function clearSlot(idx) {
    await api.delete(`/printing-board/slots/${idx}`);
    await loadData(); onSaved();
  }

  const inputCls = "bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white caret-white focus:outline-none focus:border-brand transition-colors placeholder-gray-500";

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700/50 rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
          <h3 className="font-bold text-gray-100">프린팅 보드 설정</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {(
            <div>
              <p className="text-xs text-gray-500 mb-3">칸을 눌러 실제 프린터를 배치하세요 (8 × 4). 현장 배치와 같은 위치에 놓으면 찾기 쉽습니다.</p>
              <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))` }}>
                {Array.from({ length: TOTAL_SLOTS }, (_, i) => {
                  const s = slots[i];
                  const rp = s?.realPrinter;
                  const selected = editSlot === i;
                  return (
                    <button key={i} onClick={() => openSlotEdit(i)}
                      className={`rounded-lg border p-1 text-[10px] h-16 flex flex-col items-center justify-center gap-0.5 transition-all
                        ${selected ? 'border-brand bg-brand/10'
                          : rp ? 'border-gray-700 bg-gray-800 hover:border-brand/60'
                               : 'border-dashed border-gray-800 bg-gray-900/50 hover:border-brand/40'}`}>
                      <span className="text-[9px] text-gray-600 leading-none">{i + 1}</span>
                      {rp
                        ? <>
                            <Printer size={13} className="text-brand" />
                            <span className="text-gray-200 truncate w-full px-0.5 text-center leading-tight">{s.alias || rp.name}</span>
                          </>
                        : <span className="text-gray-700 text-[10px]">비어있음</span>}
                    </button>
                  );
                })}
              </div>
              {editSlot !== null && (
                <div className="mt-4 p-4 bg-gray-800/50 rounded-xl border border-gray-700/50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-gray-200">슬롯 {editSlot + 1} 설정</span>
                    <button onClick={() => setEditSlot(null)}><X size={14} className="text-gray-400" /></button>
                  </div>
                  <div className="flex gap-2 mb-3">
                    <select value={editSlotForm.realPrinterId}
                      onChange={e => setEditSlotForm(f => ({ ...f, realPrinterId: e.target.value }))}
                      className={`flex-1 ${inputCls}`}>
                      <option value="">-- 비어있음 --</option>
                      {printers.map(p => {
                        // 다른 칸에 이미 놓인 프린터는 중복 배치되지 않도록 표시해둔다
                        const at = slots.findIndex(x => x?.realPrinterId === p.id);
                        const taken = at >= 0 && at !== editSlot;
                        return (
                          <option key={p.id} value={p.id} disabled={taken}>
                            {p.name}{p.model ? ` · ${p.model.replace('Bambu Lab ', '')}` : ''}{taken ? ` (${at + 1}번 칸)` : ''}
                          </option>
                        );
                      })}
                    </select>
                    <input value={editSlotForm.alias}
                      onChange={e => setEditSlotForm(f => ({ ...f, alias: e.target.value }))}
                      placeholder="별칭 (예: 1번기)"
                      className={`w-32 ${inputCls}`} />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={saveSlot} disabled={saving}
                      className="flex-1 bg-brand text-white rounded-lg py-1.5 text-sm font-semibold hover:bg-brand/80 disabled:opacity-50 transition-colors">
                      {saving ? '저장 중...' : '저장'}
                    </button>
                    <button onClick={() => clearSlot(editSlot)}
                      className="px-3 bg-gray-700 text-gray-400 rounded-lg py-1.5 text-sm hover:bg-red-900/40 hover:text-red-300 transition-colors">
                      비우기
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
        </div>
      </div>
    </div>
    </ModalPortal>
  );
}

// ── 메인 ─────────────────────────────────────────────────────────────────
export default function PrintingBoardPage() {
  const [board, setBoard] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [queueModal, setQueueModal] = useState(null);
  const [queuePop, setQueuePop] = useState(null);   // { slot, rect } — 카드에서 펼친 예약
  const [editTarget, setEditTarget] = useState(null);
  const [adminModal, setAdminModal] = useState(false);
  const [mqttMap, setMqttMap] = useState({});
  const [detail, setDetail] = useState(null);   // 상세를 열어둔 프린터
  const [notifyPerm, setNotifyPerm] = useState(
    () => (typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'));
  const prevStateRef = useRef(null);   // printerId → 직전 gcodeState
  const [filamentTarget, setFilamentTarget] = useState(null); // { printer, side, slot }
  const [nozzleTarget, setNozzleTarget] = useState(null);     // { printer, side }
  const [stocks, setStocks] = useState([]);
  const [nozzles, setNozzles] = useState([]);
  const [, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async () => {
    try {
      const [bRes, mRes] = await Promise.all([
        api.get('/printing-board/board'),
        api.get('/admins/me').catch(() => ({ data: null })),
      ]);
      setBoard(bRes.data);
      setMe(mRes.data);
    } finally { setLoading(false); }
  }, []);

  // 프린터 실시간 상태(MQTT). 보드 조회와 주기가 달라 따로 돈다.
  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get('/printers/monitor/status');
      const m = {};
      (r.data || []).forEach(x => { m[x.printerId ?? x.id] = x; });

      // 출력 중이던 프린터가 끝나면 OS 알림을 띄운다.
      // 첫 조회 때는 알리지 않는다 — 화면을 열자마자 예전 완료 건이 쏟아지면 곤란하다.
      const prev = prevStateRef.current;
      if (prev && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        for (const [id, cur] of Object.entries(m)) {
          const before = prev[id];
          const wasRunning = before === 'RUNNING' || before === 'PAUSE';
          if (!wasRunning) continue;
          if (cur.gcodeState === 'FINISH') notifyPrinter(id, '출력 완료', cur, m);
          else if (cur.gcodeState === 'FAILED') notifyPrinter(id, '출력 실패', cur, m);
        }
      }
      prevStateRef.current = Object.fromEntries(
        Object.entries(m).map(([id, x]) => [id, x.gcodeState]));

      setMqttMap(m);
    } catch { /* 일시 오류는 무시하고 다음 주기에 다시 */ }
  }, []);

  // 알림 본문에 프린터 이름을 쓰려면 배치 정보가 필요하다
  const boardRef = useRef(board);
  useEffect(() => { boardRef.current = board; }, [board]);

  function notifyPrinter(printerId, title, st) {
    const slot = (boardRef.current || []).find(s => String(s.realPrinter?.id) === String(printerId));
    const name = slot?.alias || slot?.realPrinter?.name || `프린터 ${printerId}`;
    const body = [st.jobName, title === '출력 실패' ? st.printErrorMsg : null]
      .filter(Boolean).join('\n') || '작업이 끝났습니다.';
    try {
      const n = new Notification(`${name} — ${title}`, { body, tag: `printer-${printerId}`, icon: '/favicon.png' });
      n.onclick = () => { window.focus(); if (slot?.realPrinter) setDetail(slot.realPrinter); n.close(); };
    } catch { /* 알림이 막혀 있으면 조용히 넘어간다 */ }
  }

  async function enableNotifications() {
    if (typeof Notification === 'undefined') return;
    const r = await Notification.requestPermission();
    setNotifyPerm(r);
    if (r === 'granted') new Notification('알림이 켜졌습니다', { body: '출력이 끝나면 여기로 알려드립니다.' });
  }

  useEffect(() => { load(); loadStatus(); }, [load, loadStatus]);
  useEffect(() => {
    const t = setInterval(load, 30000);
    const t2 = setInterval(loadStatus, 5000);
    return () => { clearInterval(t); clearInterval(t2); };
  }, [load, loadStatus]);

  // 상세 모달이 쓰는 재고 목록 (열 때 필요)
  useEffect(() => {
    api.get('/filament/stock').then(r => setStocks(r.data)).catch(() => {});
    api.get('/nozzles').then(r => setNozzles(r.data)).catch(() => {});
  }, []);

  // 필라멘트·노즐을 바꾸면 상세에 열려 있는 프린터 정보도 새로 읽어야 반영된다
  const refreshDetail = useCallback(async () => {
    try {
      const r = await api.get('/printers');
      const list = Array.isArray(r.data) ? r.data : (r.data?.rows || []);
      setDetail(d => (d ? (list.find(p => p.id === d.id) || d) : d));
      const [nz] = await Promise.all([api.get('/nozzles').catch(() => ({ data: nozzles }))]);
      setNozzles(nz.data);
      await load();
    } catch { /* 실패해도 화면은 유지 */ }
  }, [load, nozzles]);

  // 큐에 붙은 파일로 실제 출력을 시작한다. 되돌릴 수 없으므로 한 번 확인받는다.
  async function handleDeleteQueue(id) {
    if (!confirm('큐를 삭제할까요?')) return;
    await api.delete(`/printing-board/queue/${id}`);
    load();
  }

  function handleEditQueue(q) {
    setEditTarget(q);
    setQueueModal(board.find(s => s.queues?.some(qq => qq.id === q.id)) || true);
  }

  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 1024);
  useEffect(() => {
    const fn = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const placed = board.filter(s => s.realPrinter);
  const totalPrinters = placed.length;
  const activePrinters = placed.filter(s => mqttMap[s.realPrinter.id]?.gcodeState === 'RUNNING').length;

  // 오류가 난 프린터 — 22대를 훑지 않아도 위에서 바로 보이게 한다
  const errored = placed.filter(s => {
    const m = mqttMap[s.realPrinter.id];
    return m?.printErrorMsg || m?.gcodeState === 'FAILED';
  });

  // 출력이 끝났는데 다음 파일이 대기 중인 프린터 — 사람이 베드를 비워줘야 이어진다

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-2 border-brand border-t-transparent rounded-full" />
    </div>
  );

  const isSuperAdmin = me?.role === 'superadmin';

  return (
    <div className={`flex flex-col ${isDesktop ? 'h-full' : 'gap-0'}`}>
      <PageHeader icon={Grid3X3} title="프린팅 현황" count={totalPrinters > 0 ? `${activePrinters}/${totalPrinters}` : undefined}>
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <span className="hidden sm:block">{dayjs().format('HH:mm')} 기준</span>
          <button onClick={load} className="p-1.5 hover:text-gray-300 hover:bg-gray-800 rounded-lg transition-colors">
            <RefreshCw size={13} />
          </button>
          {/* 출력 완료·실패를 OS 알림으로 받는다. 브라우저가 열려 있어야 동작한다. */}
          {notifyPerm !== 'unsupported' && (
            notifyPerm === 'granted' ? (
              <span title="출력이 끝나면 알림이 옵니다" className="p-1.5 text-brand"><Bell size={13} /></span>
            ) : notifyPerm === 'denied' ? (
              <span title="브라우저에서 알림이 차단돼 있습니다. 주소창 옆 자물쇠에서 허용해 주세요."
                className="p-1.5 text-gray-600"><BellOff size={13} /></span>
            ) : (
              <button onClick={enableNotifications} title="출력 완료 알림 켜기"
                className="p-1.5 hover:text-brand hover:bg-gray-800 rounded-lg transition-colors">
                <BellOff size={13} />
              </button>
            )
          )}
        </div>
        {isSuperAdmin && (
          <button onClick={() => setAdminModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gray-800 border border-white/8 rounded-lg text-gray-400 hover:border-brand/40 hover:text-brand transition-colors">
            <Settings size={12} /> 배치 설정
          </button>
        )}
      </PageHeader>

      {/* 오류부터 위에 세운다 — 회수 대기보다 급하다 */}
      {errored.length > 0 && (
        <div className="flex items-center gap-2 mb-2 px-3 py-2 rounded-xl bg-red-500/15 border border-red-500/40 shrink-0">
          <AlertTriangle size={14} className="text-red-500 shrink-0" />
          <span className="text-xs text-red-600 font-semibold shrink-0">{errored.length}대 오류</span>
          <span className="text-xs text-gray-500 truncate">
            {errored.map(s => `${s.alias || s.realPrinter.name}: ${mqttMap[s.realPrinter.id]?.printErrorMsg || '출력 실패'}`).join(' · ')}
          </span>
        </div>
      )}

      {/* 손봐야 할 프린터를 맨 위에 모아 보여준다 */}

      {/* 그리드 — 모바일: 2열 스크롤 / 데스크탑: 8×3 전체높이 */}
      <div
        className={isDesktop ? 'flex-1 min-h-0 grid gap-2' : 'grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-4'}
        style={isDesktop ? {
          gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${ROWS}, minmax(0, 1fr))`,
        } : {}}>
        {/* 데스크탑은 현장 배치도라 빈 칸도 그대로 보여준다.
            모바일은 세로로 쌓이므로 빈 칸을 보여주면 스크롤만 길어진다. */}
        {(isDesktop ? board : board.filter(s => s.realPrinter)).map((slot, i) => (
          <PrinterCard
            key={slot.slotIndex}
            slot={slot}
            me={me}
            mqtt={slot.realPrinter ? mqttMap[slot.realPrinter.id] : null}
            camIndex={i}
            onOpenDetail={setDetail}
            isMobile={!isDesktop}
            onOpenQueue={(s, el) => setQueuePop({ slot: s, rect: el.getBoundingClientRect() })}
          />
        ))}
      </div>

      {!isDesktop && board.every(s => !s.realPrinter) && (
        <div className="flex flex-col items-center justify-center py-16 gap-2 text-center">
          <Printer size={28} className="text-gray-700" />
          <p className="text-sm text-gray-500">배치된 프린터가 없습니다</p>
          <p className="text-xs text-gray-600">배치 설정에서 칸마다 프린터를 지정해 주세요</p>
        </div>
      )}

      {queuePop && (
        <QueuePopover
          slot={board.find(b => b.slotIndex === queuePop.slot.slotIndex) || queuePop.slot}
          anchorRect={queuePop.rect}
          me={me}
          onClose={() => setQueuePop(null)}
          onAdd={s => { setQueuePop(null); setEditTarget(null); setQueueModal(s); }}
          onEdit={q => { setQueuePop(null); handleEditQueue(q); }}
          onDelete={async id => { await handleDeleteQueue(id); }}
        />
      )}

      {queueModal && (
        <QueueModal
          slot={typeof queueModal === 'object' && queueModal !== true ? queueModal : null}
          editTarget={editTarget}
          onClose={() => { setQueueModal(null); setEditTarget(null); }}
          onSaved={() => { setQueueModal(null); setEditTarget(null); load(); }}
        />
      )}
      {adminModal && <AdminModal onClose={() => setAdminModal(false)} onSaved={load} />}

      {detail && (
        <PrinterDetailModal
          printer={detail}
          mqtt={mqttMap[detail.id] || {}}
          stocks={stocks}
          nozzles={nozzles}
          errorMsg={getErrorMsg(mqttMap[detail.id])}
          onClose={() => setDetail(null)}
          onFilament={(pr, side, slot) => setFilamentTarget({ printer: pr, side, slot })}
          onNozzle={(pr, side) => setNozzleTarget({ printer: pr, side })}
          onControl={async (cmd) => {
            try {
              await api.post(`/printers/${detail.id}/control`, { command: cmd });
              await loadStatus();
            } catch (e) { alert('제어 실패: ' + (e.response?.data?.message || e.message)); }
          }}
        />
      )}

      {filamentTarget && (
        <ModalPortal><FilamentModal
          printer={filamentTarget.printer}
          side={filamentTarget.side}
          slot={filamentTarget.slot}
          stocks={stocks}
          onClose={() => setFilamentTarget(null)}
          onSave={async () => { setFilamentTarget(null); await refreshDetail(); }}
        /></ModalPortal>
      )}

      {nozzleTarget && (
        <ModalPortal><NozzleSelectModal
          printer={nozzleTarget.printer}
          side={nozzleTarget.side}
          nozzles={nozzles}
          onClose={() => setNozzleTarget(null)}
          onSave={async () => { setNozzleTarget(null); await refreshDetail(); }}
        /></ModalPortal>
      )}

    </div>
  );
}
